/**
 * MarketplaceSourcesSettings — Manage marketplace plugin sources.
 * Built-in sources can be toggled on/off. Custom sources can be added/removed.
 */

import { useState, useEffect, useCallback } from 'react';
import { Globe, Trash2, Plus, RotateCcw } from 'lucide-react';
import { Panel, PanelHeader, PanelBody } from '../shared/Panel';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useMarketplaceSources, useUpdateMarketplaceSources } from '../../api/workspace-marketplace-sources';
import type { MarketplaceSourceConfig } from '../../api/plugins';

function generateKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || `custom-${Date.now()}`;
}

export function MarketplaceSourcesSettings() {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { data: sources, isLoading } = useMarketplaceSources(workspaceId);
  const updateSources = useUpdateMarketplaceSources(workspaceId);

  const [draft, setDraft] = useState<MarketplaceSourceConfig[]>([]);
  const [dirty, setDirty] = useState(false);

  // Add-source form state
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [addError, setAddError] = useState('');

  // Sync from server
  useEffect(() => {
    if (sources) {
      setDraft(sources);
      setDirty(false);
    }
  }, [sources]);

  const handleToggle = useCallback((key: string) => {
    setDraft(prev => prev.map(s => s.key === key ? { ...s, disabled: !s.disabled } : s));
    setDirty(true);
  }, []);

  const handleRemove = useCallback((key: string) => {
    setDraft(prev => prev.filter(s => s.key !== key));
    setDirty(true);
  }, []);

  const handleAdd = useCallback(() => {
    const label = newLabel.trim();
    const url = newUrl.trim();
    if (!label) { setAddError('Name is required'); return; }
    if (!url || !url.startsWith('https://')) { setAddError('URL must start with https://'); return; }
    const key = generateKey(label);
    if (draft.some(s => s.key === key)) { setAddError('A source with this name already exists'); return; }

    setDraft(prev => [...prev, { key, label, url, isBuiltin: false, disabled: false }]);
    setNewLabel('');
    setNewUrl('');
    setAddError('');
    setDirty(true);
  }, [newLabel, newUrl, draft]);

  const handleSave = useCallback(() => {
    updateSources.mutate(draft);
    setDirty(false);
  }, [draft, updateSources]);

  const handleReset = useCallback(() => {
    if (sources) {
      setDraft(sources);
      setDirty(false);
    }
  }, [sources]);

  return (
    <Panel>
      <PanelHeader
        title="Marketplace Sources"
        subtitle="Manage where plugins are fetched from. Toggle built-in sources or add custom ones."
      />
      <PanelBody>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-surface-subtle rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* Source list */}
            <div className="space-y-1.5">
              {draft.map(src => (
                <div
                  key={src.key}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                    src.disabled ? 'border-line bg-surface-muted opacity-60' : 'border-line bg-white'
                  }`}
                >
                  <div className="w-7 h-7 rounded-md bg-surface-subtle flex items-center justify-center shrink-0">
                    <Globe size={13} className="text-subtle" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] font-semibold text-text">{src.label}</span>
                      {src.isBuiltin && (
                        <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-surface-subtle text-muted uppercase">
                          built-in
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-subtle truncate mt-0.5" title={src.url}>
                      {src.url}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleToggle(src.key)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                        !src.disabled ? 'bg-coral-500' : 'bg-neutral-300'
                      }`}
                      title={src.disabled ? 'Disabled — click to enable' : 'Enabled — click to disable'}
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        !src.disabled ? 'translate-x-4' : 'translate-x-0'
                      }`} />
                    </button>
                    {!src.isBuiltin && (
                      <button
                        onClick={() => handleRemove(src.key)}
                        className="p-1 text-subtle hover:text-red-500 hover:bg-red-50 rounded transition-all"
                        title="Remove source"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add custom source */}
            <div className="mt-4 p-3 border border-dashed border-line rounded-lg bg-surface-muted/50">
              <p className="text-[11px] font-semibold text-muted mb-2 flex items-center gap-1.5">
                <Plus size={12} /> Add Custom Source
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newLabel}
                  onChange={e => { setNewLabel(e.target.value); setAddError(''); }}
                  placeholder="Source name"
                  className="flex-[2] px-2.5 py-1.5 border border-line rounded-lg text-[12px] text-text focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none"
                />
                <input
                  type="text"
                  value={newUrl}
                  onChange={e => { setNewUrl(e.target.value); setAddError(''); }}
                  placeholder="https://raw.githubusercontent.com/..."
                  className="flex-[5] px-2.5 py-1.5 border border-line rounded-lg text-[12px] text-text focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none"
                />
                <button
                  onClick={handleAdd}
                  className="px-3 py-1.5 text-[11px] font-semibold text-white bg-coral-500 hover:bg-coral-600 rounded-lg transition-colors shrink-0"
                >
                  Add
                </button>
              </div>
              {addError && (
                <p className="text-[10px] text-red-500 mt-1.5">{addError}</p>
              )}
            </div>

            {/* Dirty-state save bar */}
            {dirty && (
              <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-line">
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-muted hover:text-text bg-surface-subtle hover:bg-surface-subtle rounded-lg transition-colors"
                >
                  <RotateCcw size={11} /> Reset
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateSources.isPending}
                  className="px-4 py-1.5 text-[11px] font-semibold text-white bg-coral-500 hover:bg-coral-600 rounded-lg transition-colors disabled:opacity-50"
                >
                  {updateSources.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </>
        )}
      </PanelBody>
    </Panel>
  );
}
